import { useState, useContext, useEffect, memo } from "react";
import { Button, Card, Badge, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paperclip, Star } from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import doctor1 from "../assets/img/avatars/logo_unknown_doctor.jpg";
import NotyfContext from "../contexts/NotyfContext";
import TaskModal from "../utils/TaskModal";
import LastActivityDisplay from "../utils/LastActivityDisplay";
import TaskStatusBadge from "../utils/TaskStatusBadge";
import TaskAdditionalActionsModal from "./TaskAdditionalActionsModal";
import getAvailableCarrierSubmissionActionTypeDisplay from "./autoActionTypes";
import rawPsvTypes from "./psvTypes";

import authenticatedFetch from "../utils/fetch"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import TaskAdditionalInstructionsModal from "./TaskAdditionalInstructionsModal";

const Task = memo(({ reloadTaskLists, task, showModal=false, showDue=true, attachments=[], blocked=false, hideProviderName=false, hidePracticeName=false, allowModal=true, shortLastActivity=false }) => {
    const category = task.category.value;
    const psvType = task.psvType;
    const state = task.state;
    const badge = task.category.label;
  
    const assigneeDisplay = []; 
  
    const { isH3Admin, authorities } = useContext(UserSettingsContext);
    const canEdit = isH3Admin || authorities.includes("TASK_EDIT");
  
    const [show, setShow] = useState(showModal);
    const [showAdditionalActionsModal, setShowAdditionalActionsModal] = useState(false);
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    
    const [searchParams] = useSearchParams();
    const [updateStarProcessing, setUpdateStarProcessing] = useState(false);

    const navigate = useNavigate();
    const notyf = useContext(NotyfContext);

    useEffect(() => {
      if (showModal && searchParams.has('task'))
      {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('task');
        navigate({ search: newParams.toString() }, { replace: true });
      }
    }, [showModal, searchParams, navigate]);

    const handleShow = () => setShow(true);
    const handleCancel = () => {
      setShow(false);
    }
  
    const handleSave = () => {
      setShow(false);
      reloadTaskLists();
    }

    const handleRefresh = () => {
      reloadTaskLists();
    }
  
    const handleDelete = () => {
      setShow(false);
      reloadTaskLists();
    }

    const handleTaskCardClicked = (e) => {
        if (!allowModal) {
            // Assume coming from a read-only view (i.e. provider screen), take to tasks screen
            window.location = "/tasks?task=" + task.id;
        }
    }

    const handleShowAdditionalActions = (e) => { 
        e.preventDefault();
        setShowAdditionalActionsModal(true);
    }

    const handleAdditionalActionsModalClose = () => {
        setShowAdditionalActionsModal(false);
    }

    const handleShowInstructions = (e) => {
        e.preventDefault();
        setShowInstructionsModal(true);
    }

    const handleInstructionsModalClose = () => {
        setShowInstructionsModal(false);
    }

    const handleStarClick = (e, id) => {
      e.preventDefault();
      if (!canEdit || !allowModal)
      {
        return;
      }

      task.starred = !task.starred; // Update locally for better UX while waiting for the resort
      setUpdateStarProcessing(true);
      authenticatedFetch({
        path: "task/star/" + id,
        method: "PUT",
        successHandler: function(result) {
          reloadTaskLists();
          setUpdateStarProcessing(false);
        },
        errorHandler: function(error) {
          setUpdateStarProcessing(false);
          task.starred = !task.starred;
          reloadTaskLists(); // re-render the star
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }

    var avatarDisplay;
  
    // TODO decide if we also show practice assignee avatar here, and then either way need to flag them in the other spot too
    if (task.assignees && task.assignees.length > 0) {
      avatarDisplay = task.assignees.map((assignee, i) => {
        assigneeDisplay.push(assignee.firstName + ' ' + assignee.lastName);
        
        return (
          <img
            key={i}
            src={assignee.photoUrl == null || assignee.photoUrl === '' ? doctor1 : assignee.photoUrl}
            width="32"
            height="32"
            className="rounded-circle"
            alt={assignee.firstName + ' ' + assignee.lastName}
            title={assignee.firstName + ' ' + assignee.lastName}
          />
        )
      }, this);
    }
  
    var dueDateDisplay = '';
    var dueColors = { background: '#D9E5E2', color: '#2F4F4A' };
    if (showDue && task.due != null)
    {
      var dueParts = task.due.split("-");
      var dueDate = new Date(dueParts[0], dueParts[1] - 1, dueParts[2]);
      dueDateDisplay = dueDate.toLocaleDateString("en-US");

      const diffInMs   = dueDate - new Date()
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

      if (diffInDays <= -1)
      {
        dueColors = { background: '#F8E1DF', color: '#8A2F29' };
      }
      else if (diffInDays <= 4)
      {
        dueColors = { background: '#FBEBD3', color: '#7A4E0E' };
      }
    }
  
    var psvTypeDisplay = "";
    for (var i = 0; i < rawPsvTypes.length; i++) {
      if (rawPsvTypes[i].value === task.psvType) {
        psvTypeDisplay = rawPsvTypes[i].label;
        break;
      }
    }

    if (psvTypeDisplay !== "" && task.state != null && task.state !== "")
    {
      psvTypeDisplay += " | " + task.state;
    }

    const availableCarrierSubmissionActionTypeDisplay = getAvailableCarrierSubmissionActionTypeDisplay(task.availableCarrierSubmissionActionType);
    const availableCarrierSubmissionActionTypeErrorsDisplay = task.availableCarrierSubmissionActionTypeErrors ? task.availableCarrierSubmissionActionTypeErrors.join("; ") : "";

    const hasAdditionalInstructions = task.additionalInstructionsEntityName && task.additionalInstructionsEntityName !== "";

    return (
      <Card style={{cursor: !allowModal ? "pointer" : "default"}} onClick={(e) => handleTaskCardClicked(e)} className="mb-3 bg-light border" id={task.id} data-ordering={task.ordering}>
        <Card.Header className="p-3 bg-light ">
        <TaskStatusBadge status={task.status} className="float-start" onlyShowWhenBlocked />
         { showDue && task.due != null ?
          <>
            <Badge pill className="float-end" bg="" style={{ ...dueColors, fontWeight: 500 }}>{dueDateDisplay}</Badge><div style={{marginBottom: '8px', clear: 'right'}}></div>
          </>
          : <div style={{marginBottom: '8px', clear: "both"}}></div>
         }
          <Card.Title className="mb-0">
            {!hideProviderName && task.providerId !== 0 ? 
              (task.providerIsDeleted ? <>{task.providerFormattedName} <br /></>
              :
              <a href={"/provider/" + task.providerId} style={{color: "#495057"}}>{task.providerFormattedName}<br /></a>)
              
              : ""}

            <span style={{color: '#2B7A78', fontSize: '0.9em'}}>{task.carrierId !== 0 ? <>{task.carrierName}<br /></>: ""}</span>
            <span style={{color: '#2B7A78', fontSize: '0.9em'}}>{task.psvType !== null && task.psvType !== "" ? <>{psvTypeDisplay}<br /></>: ""}</span>
            <span style={{color: '#2B7A78', fontSize: '0.9em'}}>{task.hubId !== 0 ? <>{task.hubName}<br /></>: ""}</span>
            <span style={{color: '#2B7A78', fontSize: '0.9em'}}>{task.facilityId !== 0 ? <>{task.facilityName}<br /></>: ""}</span>
            <span style={{color: '#6c757d', fontSize: '0.9em'}}>{task.other !== null && task.other !== "" ? <>{task.other}<br /></>: ""}</span>
            
            {!hidePracticeName ? <div style={{fontSize: "0.8em", fontWeight: "normal", width: "100%", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}><a href={"/practice/" + task.practiceId} style={{color: "#495057"}}>{task.practiceName}</a></div>: ""}

            { badge != null ?
              <Badge pill className="float-start" bg="secondary" style={{marginTop: '2px'}}>
                {badge}
              </Badge> : ''
            }
            {updateStarProcessing ? <Spinner className="float-end" style={{marginTop: "4px"}} animation="border" size="sm" variant="primary"/> : (
              task.starred ? <Star className="float-end" onClick={(e) => handleStarClick(e, task.id)} style={{height: '16px', width: '16px', marginTop: "4px", cursor: (canEdit && allowModal) ? "pointer": "default", color: '#E5A54B', fill: "#E5A54B"}} /> : <Star className="float-end" onClick={(e) => handleStarClick(e, task.id)} style={{height: '16px', width: '16px', marginTop: "4px", cursor: (canEdit && allowModal) ? "pointer": "default"}} />
            )}
          </Card.Title>
          
        </Card.Header>
        <Card.Body className="p-3" style={{marginTop: '-15px'}}>
          <hr style={{marginTop: '-10px'}} />
          <LastActivityDisplay task={task} shortLastActivity={shortLastActivity} />
          { attachments.length > 0 ?
          <p style={{cursor: 'pointer'}}><Paperclip style={{height: '15px', width: '15px'}} />&nbsp;{attachments.length} attachment{attachments.length > 1 ? 's' : ''}</p>
          : ''
          }
          <div className="float-end mt-n1">
            {avatarDisplay}
          </div>
          { allowModal ?
          <Button onClick={(e) => handleShow()} variant="primary" size="sm">
            {canEdit ? "Open" : "View Details"}
          </Button>
          : <></>
          }
          { allowModal && canEdit && hasAdditionalInstructions ?
            <>&nbsp;&nbsp;<Button onClick={(e) => handleShowInstructions(e)} variant="primary" size="sm"><FontAwesomeIcon icon={faInfoCircle} style={{width: "16px", height: "16px", marginBottom: "-2px"}}/></Button></>
            : <></>
          }
          { allowModal && canEdit && availableCarrierSubmissionActionTypeDisplay !== "" && task.status !== "COMPLETE" ?
            <>&nbsp;&nbsp;<Button onClick={(e) => handleShowAdditionalActions(e)} variant="primary" size="sm"><FontAwesomeIcon icon={faRobot} style={{width: "16px", height: "16px", marginBottom: "-1px"}}/></Button></>
            : (allowModal && canEdit && availableCarrierSubmissionActionTypeErrorsDisplay && task.status !== "COMPLETE" ? 
              <OverlayTrigger
                triggers="[hover,focus]"
                placement="bottom"
                overlay={<Tooltip>{availableCarrierSubmissionActionTypeErrorsDisplay}</Tooltip>}
                >
                  <span>&nbsp;&nbsp;<Button variant="warning" size="sm"><FontAwesomeIcon icon={faRobot} style={{width: "16px", height: "16px"}}/></Button></span>
              </OverlayTrigger>
            : <></>
            )
          }
          <br />
        { allowModal ?
        <TaskModal show={show} onCancel={handleCancel} onSave={handleSave} onRefresh={handleRefresh} onDelete={handleDelete} additionalInstructionsEntityName={task.additionalInstructionsEntityName} practiceName={task.practiceName} id={task.id} category={category} psvType={psvType} state={state} practiceId={task.practiceId} carrierId={task.carrierId} 
          providerId={task.providerIsDeleted ? 0 : task.providerId} hubId={task.hubId} facilityId={task.facilityId} other={task.other} referenceNumber={task.referenceNumber} 
          assignees={task.assignees} status={task.status} due={task.due} deadline={task.deadline} archived={task.archived} tracked={task.tracked} activityList={task.activity} 
          providerIsDeleted={task.providerIsDeleted} eSignatureTokenInfo={task.eSignatureTokenInfo} documentTokenInfo={task.documentTokenInfo}/>
        : <></>
        }
        <TaskAdditionalActionsModal show={showAdditionalActionsModal} onClose={handleAdditionalActionsModalClose} id={task.id} availableCarrierSubmissionActionType={task.availableCarrierSubmissionActionType} reloadTaskLists={reloadTaskLists} />
        <TaskAdditionalInstructionsModal show={showInstructionsModal} onClose={handleInstructionsModalClose} id={task.id} category={task.category} entityName={task.additionalInstructionsEntityName} practiceName={task.practiceName} />
        </Card.Body>
      </Card>
    )
  });

  export default Task;
